import * as DialogPrimitive from '@radix-ui/react-dialog';
import {
  type PointerEvent as ReactPointerEvent,
  type ReactNode,
  useCallback,
  useRef,
  useState,
} from 'react';

import { DragHandle } from './overlay-kit.js';
import { Icon } from './icons.js';
import { useWindowClass } from './window-class.js';

export interface DialogProps {
  readonly children?: ReactNode;
  readonly description?: ReactNode;
  /** A small line above the title (prototype `.eyebrow`). */
  readonly eyebrow?: ReactNode;
  /** Actions at the bottom of the sheet (prototype `.sheet-actions`). */
  readonly footer?: ReactNode;
  /**
   * At the phone width (compact window class) the dialog covers the screen. The default is on for
   * the panel dialog (`variant="dialog"`) of every size but `small`, and off for the bottom sheet.
   */
  readonly fullScreenOnCompact?: boolean;
  readonly onOpenChange: (open: boolean) => void;
  readonly open: boolean;
  /** When false, drag-to-dismiss is disabled (for example while a command is pending). */
  readonly allowDragDismiss?: boolean;
  /**
   * The width (owner dialog-layout rules):
   * - `small` 24rem: a confirmation or a short read (stays a bottom sheet on a phone);
   * - `default` 32rem: a simple one-column form (prototype `.panel-sheet`);
   * - `wide` 42rem: a form with rows of controls side by side, or a table (`.wide-sheet`);
   * - `wizard` 52rem: a stepped flow or two panes (`.merchant-registration-sheet`).
   * Form grids inside go to two columns only when both get at least 15rem.
   */
  readonly size?: DialogSize;
  /**
   * The header and the footer are always pinned while the body scrolls; this adds the divider
   * over the footer (the action bar of a long form).
   */
  readonly stickyFooter?: boolean;
  /** Pins the header (title and «بستن») to the top of the sheet while the body scrolls (MD3). */
  readonly stickyHeader?: boolean;
  readonly title: ReactNode;
  /** A trigger element; focus returns to it on close. */
  readonly trigger?: ReactNode;
  /**
   * `dialog` is the panel sheet: a drawer at the inline end on desktop and a bottom sheet at phone
   * width (prototype `.panel-sheet`). `sheet` is the floating bottom sheet with a handle (prototype
   * `.modal-sheet`), used by the buyer app and the «بیشتر» menus.
   */
  readonly variant?: 'dialog' | 'sheet';
}

export type DialogSize = 'small' | 'default' | 'wide' | 'wizard';

const DRAG_DISMISS_PX = 96;

/**
 * Modal dialog or bottom sheet on Radix Dialog: focus is trapped inside and returned to the trigger
 * on close, Escape and the scrim close, and the title labels the dialog (FTD §16). The close control
 * is the round «بستن» icon button in the header, as in the prototype.
 */
export function Dialog({
  allowDragDismiss = true,
  children,
  description,
  eyebrow,
  footer,
  fullScreenOnCompact,
  onOpenChange,
  open,
  size = 'default',
  stickyFooter = false,
  stickyHeader = false,
  title,
  trigger,
  variant = 'dialog',
}: DialogProps) {
  const windowClass = useWindowClass('expanded');
  const contentRef = useRef<HTMLDivElement>(null);
  const dragStartY = useRef<number | null>(null);
  const [dragOffset, setDragOffset] = useState(0);
  const [dragging, setDragging] = useState(false);

  const dragEnabled = variant === 'sheet' && allowDragDismiss;
  const fullscreen =
    (fullScreenOnCompact ?? (variant === 'dialog' && size !== 'small')) &&
    windowClass === 'compact';

  const resetDrag = useCallback(() => {
    dragStartY.current = null;
    setDragOffset(0);
    setDragging(false);
    const node = contentRef.current;
    if (node !== null) {
      node.style.transform = '';
    }
  }, []);

  const onHandlePointerDown = useCallback(
    (event: ReactPointerEvent<HTMLButtonElement>) => {
      if (!dragEnabled) {
        return;
      }
      event.currentTarget.setPointerCapture(event.pointerId);
      dragStartY.current = event.clientY;
      setDragging(true);
    },
    [dragEnabled],
  );

  const onHandlePointerMove = useCallback(
    (event: ReactPointerEvent<HTMLButtonElement>) => {
      if (!dragEnabled || dragStartY.current === null) {
        return;
      }
      const delta = Math.max(0, event.clientY - dragStartY.current);
      setDragOffset(delta);
      const node = contentRef.current;
      if (node !== null) {
        node.style.transform = `translateY(${delta}px)`;
      }
    },
    [dragEnabled],
  );

  const onHandlePointerUp = useCallback(
    (event: ReactPointerEvent<HTMLButtonElement>) => {
      if (!dragEnabled) {
        return;
      }
      event.currentTarget.releasePointerCapture(event.pointerId);
      if (dragOffset >= DRAG_DISMISS_PX) {
        resetDrag();
        onOpenChange(false);
        return;
      }
      resetDrag();
    },
    [dragEnabled, dragOffset, onOpenChange, resetDrag],
  );

  return (
    <DialogPrimitive.Root
      onOpenChange={(next) => {
        if (!next) {
          resetDrag();
        }
        onOpenChange(next);
      }}
      open={open}
    >
      {trigger === undefined ? null : (
        <DialogPrimitive.Trigger asChild>{trigger}</DialogPrimitive.Trigger>
      )}
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className="saay-overlay" />
        <DialogPrimitive.Content
          className="saay-dialog saay-material-thick"
          data-dragging={dragging || undefined}
          data-fullscreen={fullscreen || undefined}
          data-size={size}
          data-sticky-header={stickyHeader || undefined}
          data-variant={variant}
          dir="rtl"
          onOpenAutoFocus={(event) => {
            if (fullscreen) {
              // A full-screen task that opens on a field (the PIN) starts typing there; otherwise
              // Radix focuses the first control (the close button).
              const field = contentRef.current?.querySelector<HTMLElement>(
                'input:not([type="hidden"]), textarea, select',
              );
              if (field) {
                event.preventDefault();
                field.focus();
              }
              return;
            }
            event.preventDefault();
            const firstField = contentRef.current?.querySelector<HTMLElement>(
              'input, textarea, select, button:not([aria-label="بستن"]):not(.saay-drag-handle)',
            );
            (firstField ?? contentRef.current)?.focus();
          }}
          ref={contentRef}
          {...(description === undefined ? { 'aria-describedby': undefined } : {})}
        >
          {variant === 'sheet' ? (
            <DragHandle
              disabled={!dragEnabled}
              onPointerDown={onHandlePointerDown}
              onPointerMove={onHandlePointerMove}
              onPointerUp={onHandlePointerUp}
            />
          ) : (
            <div aria-hidden="true" className="saay-dialog__handle" />
          )}
          <header className="saay-dialog__header">
            <div className="saay-dialog__heading">
              {eyebrow === undefined ? null : <span className="saay-eyebrow">{eyebrow}</span>}
              <DialogPrimitive.Title className="saay-dialog__title">{title}</DialogPrimitive.Title>
            </div>
            <DialogPrimitive.Close aria-label="بستن" className="saay-icon-button" title="بستن">
              <Icon name="close" />
            </DialogPrimitive.Close>
          </header>
          {description === undefined && children === undefined ? null : (
            // The one scroll container: the header and the footer stay in view, and nothing in the
            // body can widen the dialog (overflow-x is clipped, every child may shrink).
            <div className="saay-dialog__scroll">
              {description === undefined ? null : (
                <DialogPrimitive.Description className="saay-dialog__description">
                  {description}
                </DialogPrimitive.Description>
              )}
              {children === undefined ? null : <div className="saay-dialog__body">{children}</div>}
            </div>
          )}
          {footer === undefined ? null : (
            <div className="saay-dialog__footer" data-sticky={stickyFooter || undefined}>
              {footer}
            </div>
          )}
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}

/** Side sheet (C7): inline-end drawer on expanded widths, bottom sheet on compact. */
export function SideSheet(props: Omit<DialogProps, 'variant'>) {
  return <Dialog {...props} variant="dialog" />;
}

export function Sheet(props: Omit<DialogProps, 'variant'>) {
  return <Dialog {...props} variant="sheet" />;
}

/** Bottom sheet (C8) with a drag handle and optional snap-to-dismiss. */
export function BottomSheet(props: Omit<DialogProps, 'variant'>) {
  return <Dialog {...props} variant="sheet" />;
}
